import type { AnalyticsPlugin } from 'analytics'
import type { AnalyticsPayload, SuperProperties } from '../../../models/Analytics'
import { AnalyticsServiceSettings } from '../../../models/AnalyticsServiceSettings'
import { flattenObjectForTealium } from '../helpers/data-layer-converters'

interface TealiumPluginConfig {
  isEmbedded: boolean
  campaignId: string
  organizationId: string
}

interface TealiumSettings {
  account: string
  profile: string
}

type TealiumSuperProperties = SuperProperties &
  TealiumSettings & {
    [key: string]: unknown
  }

type PluginData = {
  config: TealiumSuperProperties
  payload: AnalyticsPayload
}

type TealiumPluginData = PluginData & {
  config: TealiumSuperProperties
}

/**
 * Creates Tealium plugin with proper configuration
 * !!! --> Only init when not embedded (not in iframe context)
 * (if embedded, Tealium events are handled by cross-frame-analytics)
 */
export const makeTealiumAnalyticsPlugin = (
  settings: AnalyticsServiceSettings[],
  config: TealiumPluginConfig,
): AnalyticsPlugin | undefined => {
  try {
    if (config.isEmbedded) {
      return undefined
    }

    const tealiumSettings = getTealiumSettings(settings)

    if (!tealiumSettings) {
      return undefined
    }

    const superProperties = {
      campaignId: config.campaignId,
      orgId: config.organizationId,
      environment: process.env.APP_ENV ?? 'dev',
      hasCartEnabled: false,
    }

    return tealium({ ...tealiumSettings, ...superProperties })
  } catch (error) {
    console.error('Failed to initialize Tealium plugin:', error)
    return undefined
  }
}

/**
 * Extracts and validates Tealium settings from analytics settings array
 */
function getTealiumSettings(settings: AnalyticsServiceSettings[]): TealiumSettings | null {
  const tealiumSetting = settings.find((setting) => setting.service_name === 'tealium') as
    | AnalyticsServiceSettings<TealiumSettings, object>
    | undefined

  // Basic validation - ensure account and profile exist and are non-empty strings
  if (
    !tealiumSetting?.parameters?.account ||
    typeof tealiumSetting.parameters.account !== 'string' ||
    tealiumSetting.parameters.account.trim() === ''
  ) {
    console.warn('Tealium plugin: account is required but not provided')
    return null
  }

  if (
    !tealiumSetting?.parameters?.profile ||
    typeof tealiumSetting.parameters.profile !== 'string' ||
    tealiumSetting.parameters.profile.trim() === ''
  ) {
    console.warn('Tealium plugin: profile is required but not provided')
    return null
  }

  return tealiumSetting.parameters
}

/**
 * Custom Tealium analytics plugin - consistent with whammy and classy-checkout
 * Internal function used by makeTealiumAnalyticsPlugin
 */
const tealium = (settings: TealiumSuperProperties): AnalyticsPlugin => ({
  name: 'tealium',
  config: flattenObjectForTealium(settings as Record<string, unknown>),

  initialize({ config }: TealiumPluginData): void {
    const { account, profile, ...superProperties } = config

    // Create Tealium Settings object
    window.utag_cfg_ovrd = window.utag_cfg_ovrd || {}
    // Disable the automatic tracking call on initial page load.
    window.utag_cfg_ovrd.noview = true

    // Init Tealium Script
    const tealiumScript = document.createElement('script')
    tealiumScript.id = 'classy-tealium-script'
    tealiumScript.type = 'text/javascript'
    tealiumScript.async = true
    tealiumScript.src = `https://tags.tiqcdn.com/utag/${account}/${profile}/${superProperties.environment}/utag.js`

    // Inject Tealium Script into the DOM
    document.body.prepend(tealiumScript)

    // Init Universal Data Object Script
    const utagScript = document.createElement('script')
    utagScript.id = 'classy-tealium-utag-object'
    utagScript.type = 'text/javascript'
    utagScript.async = true
    utagScript.text = `var utag_data = ${JSON.stringify(superProperties)}`

    // Inject Universal Data Object Script into the DOM
    document.body.prepend(utagScript)

    // Init A/B Test Script
    const abTestScript = document.createElement('script')
    abTestScript.id = 'classy-tealium-sync-script'
    abTestScript.type = 'text/javascript'
    abTestScript.src = `https://tags.tiqcdn.com/utag/${account}/${profile}/${superProperties.environment}/utag.sync.js`

    // Inject A/B Test Script into the <head> tag of the DOM
    document.head.prepend(abTestScript)

    /**
     * Heap Fix: Overriding setAttribute to block Tealium from loading a second Heap script
     * 1. Access the setAttribute for all script elements via the prototype chain
     * 2. Override setAttribute method with custom logic that checks for Tealium setting Heap src
     * 3. Shim the container's heap.push method to avoid a console error
     * 4. Empty return statement to prevent setting the src
     * 5. Finally we add a call to provide the default behavior for other attributes
     *
     * Overriding setAttribute like this can only be done once. If another analytics plugin also
     * needs to override it, then the last plugin to do so will be the one that is used.
     */
    const originalSetAttribute = HTMLScriptElement.prototype.setAttribute

    HTMLScriptElement.prototype.setAttribute = function (name, value) {
      if (
        // skip classy scripts where id starts with 'classy'
        this.id.substring(0, 6) !== 'classy' &&
        name === 'src' &&
        value.substring(0, 23) === '//cdn.heapanalytics.com'
      ) {
        this.innerHTML = 'window.heap.push = () => {};'
        return
      }

      originalSetAttribute.call(this, name, value)
    }
  },
  page({ payload }: TealiumPluginData): void {
    const flatProperties = flattenObjectForTealium(payload.properties)
    window.utag.view(flatProperties)
  },
  track({ payload }: TealiumPluginData): void {
    const flatProperties = flattenObjectForTealium({
      tealium_event: payload.event,
      ...payload.properties,
    })

    window.utag.link(flatProperties)
  },
  loaded() {
    return !!window.utag
  },
})
